import React from "react";
import { useTheme } from "@mui/styles";
import {
  ListItem,
  Typography
} from "@mui/material";
import {
  ColorPickerWithLabelInline,
  DropdownWithLabelInline,
  SwitchWithLabelInline,
  TextFieldWithLabelInline
} from "../../components";
import { useTranslation } from "react-i18next";
import { ControlType } from "../../types/controls";
import { AerialMeasureType, SamplingRate, VolumeType } from "../../types/measurements";
import { SingleSlider } from "../sliders";


interface SectionTitleProps {
  text: string;
}

export const SectionTitle = (props: SectionTitleProps) => {
  const theme = useTheme();

  return (
    <ListItem style={{
      marginTop: theme.spacing(1)
    }}>
      <Typography gutterBottom style={{
        fontWeight: 'bold',
        display: "inline-block",
        fontSize: '0.9em'
      }}>
        {props.text}
      </Typography>
    </ListItem>
  );
};

interface ViewerBackgroundProps {
  value: string;
  setValue: React.Dispatch<string>
}

export const ViewerBackground = (props: ViewerBackgroundProps) => {
  const {t} = useTranslation();

  return (
    <ColorPickerWithLabelInline
      title={t('global-settings.viewer_background')}
      {...props}
    />
  );
};

interface AerialBackgroundProps {
  value: string;
  setValue: React.Dispatch<string>
}

export const AerialBackground = (props: AerialBackgroundProps) => {
  const {t} = useTranslation();

  return (
    <ColorPickerWithLabelInline
      title={t('global-settings.aerial_background')}
      {...props}
    />
  );
};

interface MouseControlTypeProps {
  value: ControlType;
  setValue: React.Dispatch<ControlType>
}

export const MouseControlType = (props: MouseControlTypeProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <DropdownWithLabelInline
      title={t("global-settings.control-type")}
      value={value}
      onChange={setValue}
      options={[
        [ControlType.Encompass, t("control-type.default")],
        [ControlType.Reverse, t("control-type.reverse")],
        [ControlType.Autocad, t("control-type.autocad")]
      ]}
    />
  );
};

interface QuickNavigationToggleProps {
  value: boolean;
  setValue: React.Dispatch<boolean>
}

export const QuickNavigationToggle = (props: QuickNavigationToggleProps) => {
  const {t} = useTranslation();

  return (
    <SwitchWithLabelInline
      title={t('global-settings.allow_quick_navigation_menu')}
      {...props}
    />
  );
};

interface AerialTypeDropdownProps {
  value: AerialMeasureType;
  setValue: React.Dispatch<AerialMeasureType>
}

export const AerialTypeDropdown = (props: AerialTypeDropdownProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <DropdownWithLabelInline
      title={t('global-settings.aerial_measurement_type')}
      value={value}
      onChange={setValue}
      options={[
        [AerialMeasureType.Spherical, t('global-settings.aerial_spherical')],
        [AerialMeasureType.Planar, t('global-settings.aerial_planar')]
      ]}
    />
  );
};

interface VolumeCalculationProps {
  value: VolumeType;
  setValue: React.Dispatch<VolumeType>
}

export const VolumeCalculation = (props: VolumeCalculationProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <DropdownWithLabelInline
      title={t('global-settings.volume_calculation')}
      value={value}
      onChange={setValue}
      options={[
        [VolumeType.MultiPlane, t('measure.volume_multi_plane')],
        [VolumeType.LowestPlane, t('measure.volume_lowest_plane')]
      ]}
    />
  );
};

interface VolumeSamplingProps {
  value: SamplingRate;
  setValue: React.Dispatch<SamplingRate>
}

export const VolumeSampling = (props: VolumeSamplingProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <DropdownWithLabelInline
      title={t('global-settings.volume_sample_rate')}
      value={value}
      onChange={setValue}
      options={[
        [SamplingRate.High, t('general.high')],
        [SamplingRate.Medium, t('general.medium')],
        [SamplingRate.Low, t('general.low')]
      ]}
    />
  );
};

interface EngineCloudURLProps {
  value: string;
  setValue: React.Dispatch<string>
}

export const EngineCloudURL = (props: EngineCloudURLProps) => {
  const {t} = useTranslation();

  return (
    <TextFieldWithLabelInline
      title={t("global-settings.engine_cloud_url")}
      {...props}
    />
  );
};

interface OrthomosaicOpacityProps {
  value: number;
  setValue: React.Dispatch<number>
}

export const OrthomosaicOpacity = (props: OrthomosaicOpacityProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <ListItem>
      <div style={{width: "100%"}}>
        <Typography gutterBottom variant="subtitle2">
          {t("global-settings.orthomosaic-opacity")}
        </Typography>

        <SingleSlider
          value={value}
          onChange={setValue}
          min={0}
          max={1}
          step={0.01}
          magnitude={100}
          endAdornment={"%"}
        />
      </div>
    </ListItem>
  );
};

interface OrthomosaicQualityProps {
  value: number;
  setValue: React.Dispatch<number>
}

export const OrthomosaicQuality = (props: OrthomosaicQualityProps) => {
  const {value, setValue} = props;
  const {t} = useTranslation();

  return (
    <ListItem>
      <div style={{width: "100%"}}>
        <Typography gutterBottom variant="subtitle2">
          {t("global-settings.orthomosaic-quality")}
        </Typography>

        <SingleSlider
          value={value}
          onChange={setValue}
          min={0.25}
          max={1.00}
          step={0.01}
          magnitude={100}
          endAdornment={"%"}
        />
      </div>
    </ListItem>
  );
};